

interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string,
        type?: string,
        title?: string,
        description?: string,
        url?: string,
        site_name?: string,
        updated_time?: string,
        image?: string,
        image_secure_url?: string,
        image_width?: number,
        image_height?: number,
        image_alt?: string,
        image_type?: string,
        video?: string,
        video_duration?: number,
        video_upload_date?: string,
        video_allow_embed?: boolean,
    };
    twitter?: {
        card?: string,
        title?: string,
        description?: string,
        image?: string,
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface injectionOptions {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface LanguageContent {
    url?: string; // Added url property
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    // aftercareTips: ListSection;
    faqData: FAQSection;
    // treatmentAreas1: TreatmentArea[];
    // treatmentAreas2: TreatmentArea[];
    injectionOptions: injectionOptions;
}

interface SharedData {
    images: {
        main: string,
        beforeAfter: string[] | any,
        introductionImage?: string,
    };
    product: {
        id: number,
        title: string,
        price: number,
        description: string,
    };
}

export const postNatal: {
    en: LanguageContent,
    ar: LanguageContent,
    shared: SharedData,
} =
{
    "en": {
        "url": "postnatal-weight-loss",
        "meta": {
            "title": "Postnatal Weight Loss in Dubai - Elite Body Home",
            "description": "Specialized postnatal weight loss program for new mothers in Dubai. Our tailored approach combines nutrition, exercise and non-invasive treatments to help you regain confidence safely.",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/services/postnatal-weight-loss-in-dubai/",
            "og": {
                "locale": "en_US",
                "type": "article",
                "title": "Postnatal Weight Loss in Dubai - Elite Body Home",
                "description": "Specialized postnatal weight loss program for new mothers in Dubai. Our tailored approach combines nutrition, exercise and non-invasive treatments.",
                "url": "https://elitebodyhome.com/services/postnatal-weight-loss-in-dubai/",
                "site_name": "elitebodyhome.com",
                "updated_time": "2025-01-23T09:27:39+00:00",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Postnatal-Weight-Loss-1.webp",
                "image_secure_url": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Postnatal-Weight-Loss-1.webp",
                "image_width": 1254,
                "image_height": 700,
                "image_alt": "Postnatal Weight Loss in Dubai",
                "image_type": "image/webp"
            },
            "twitter": {
                "card": "summary_large_image",
                "title": "Postnatal Weight Loss in Dubai - Elite Body Home",
                "description": "Specialized postnatal weight loss program for new mothers in Dubai. Our tailored approach combines nutrition, exercise and non-invasive treatments.",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Postnatal-Weight-Loss-1.webp"
            }
        },
        "breadcrumbs": ["Home", "Services", "Postnatal Weight Loss"],
        "hero": {
            "title": "Postnatal Weight Loss in Dubai",
            "subtitle": "Reclaim Your Confidence!",
            "description": "At Elite Body Home, we understand the unique postnatal weight loss challenges new mothers face. Our specialized program is tailored to help you lose weight healthily through personalized treatments and expert guidance. Whether you need a customized nutrition plan or non-invasive body contouring, we provide sustainable solutions in a supportive environment."
        },
        "howItWorks": {
            "title": "What Is Postnatal Weight Loss?",
            "description": "Postnatal weight loss should be gradual, allowing your body time to heal after childbirth. Dramatic weight loss or crash dieting can be harmful, especially for nursing mothers. Our approach combines nutrient-rich diets with appropriate fitness activities to support both weight loss and overall postpartum well-being."
        },
        "procedureSteps": {
            "heading": "Our Postnatal Weight Loss Process",
            "para": "Losing baby weight postnatally requires a balanced approach combining diet, exercise and lifestyle changes for lasting results.",
            "steps": [
                {
                    "title": "Personalized Assessment",
                    "desc": "We begin with a comprehensive evaluation of your postpartum health, weight loss goals, and lifestyle factors.",
                    "icon": "fa-solid fa-clipboard-user"
                },
                {
                    "title": "Nutrition Planning",
                    "desc": "Our experts create a customized meal plan rich in whole foods to support energy needs and healthy weight loss.",
                    "icon": "fa-solid fa-utensils"
                },
                {
                    "title": "Exercise Guidance",
                    "desc": "We recommend safe, low-impact exercises tailored to your postpartum recovery stage and fitness level.",
                    "icon": "fa-solid fa-person-walking"
                },
                {
                    "title": "Ongoing Support",
                    "desc": "Regular follow-ups to adjust your plan, celebrate progress, and address any challenges you face.",
                    "icon": "fa-solid fa-hands-holding-heart"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits of Postnatal Weight Loss",
            "subheading": "Why focus on healthy postpartum weight management?",
            "para": "Achieving a healthy postpartum weight offers numerous physical and mental health advantages beyond appearance.",
            "items": [
                {
                    "title": "Improved Energy Levels",
                    "desc": "Shedding extra pounds boosts energy to better handle the demands of motherhood.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "Reduced Health Risks",
                    "desc": "Lowers chances of developing conditions like diabetes, heart disease and high blood pressure linked to pregnancy weight gain.",
                    "icon": "fa-solid fa-heart-pulse"
                },
                {
                    "title": "Enhanced Mental Wellbeing",
                    "desc": "Achieving weight goals can boost self-esteem and alleviate postpartum depression or anxiety.",
                    "icon": "fa-solid fa-brain"
                },
                {
                    "title": "Better Mobility",
                    "desc": "Makes physical activities easier and helps regain pre-pregnancy fitness levels.",
                    "icon": "fa-solid fa-person-running"
                }
            ]
        },
        "injectionOptions": {
            "heading": "Weight Loss Injection Options",
            "subheading": "Safe, medically-supervised solutions to complement your postnatal weight loss",
            "para": "We offer various weight loss injections tailored to individual postpartum needs and goals.",
            "items": [
                {
                    "title": "Fat Burning Injections",
                    "desc": "Stimulate metabolism to target stubborn fat in areas like abdomen and thighs while being safe for postpartum bodies.",
                    "icon": "fa-solid fa-fire-flame-curved"
                },
                {
                    "title": "Lipotropic Injections",
                    "desc": "Vitamin-rich formulas that promote fat metabolism and liver function while supporting energy levels for new mothers.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "Aqualyx Fat Dissolving",
                    "desc": "Non-surgical treatment that dissolves fat in specific areas like chin and thighs, perfect for targeting postpartum problem zones.",
                    "icon": "fa-solid fa-droplet"
                },
                {
                    "title": "Nutritional IV Therapy",
                    "desc": "Replenishes essential vitamins and minerals that may be depleted after pregnancy and childbirth.",
                    "icon": "fa-solid fa-iv"
                }
            ]
        },
        "eligibility": {
            "heading": "Postnatal Weight Loss Eligibility",
            "subheading": "When can you safely begin your postpartum weight loss journey?",
            "para": "Most women can start gentle weight loss soon after delivery, but timing varies based on individual factors.",
            "items": [
                {
                    "title": "Vaginal Delivery",
                    "desc": "Typically can begin light exercises within a few weeks postpartum, after medical clearance.",
                    "icon": "fa-solid fa-baby"
                },
                {
                    "title": "Cesarean Delivery",
                    "desc": "Usually requires 6-8 weeks recovery before starting exercise, depending on healing progress.",
                    "icon": "fa-solid fa-hospital-user"
                },
                {
                    "title": "Breastfeeding Mothers",
                    "desc": "Need special nutritional considerations to maintain milk supply while losing weight safely.",
                    "icon": "fa-solid fa-bottle-baby"
                }
            ]
        },
        "preparationTips": {
            "heading": "Preparing for Postnatal Weight Loss",
            "subheading": "Setting yourself up for success",
            "para": "Proper preparation makes your postpartum weight loss journey safer and more effective.",
            "items": [
                {
                    "title": "Medical Clearance",
                    "desc": "Always consult your doctor before starting any postpartum weight loss program.",
                    "icon": "fa-solid fa-user-doctor"
                },
                {
                    "title": "Realistic Goal Setting",
                    "desc": "Aim for gradual, sustainable weight loss rather than rapid results.",
                    "icon": "fa-solid fa-bullseye"
                },
                {
                    "title": "Support System",
                    "desc": "Build a network of family, friends or fellow new moms for motivation and accountability.",
                    "icon": "fa-solid fa-people-group"
                },
                {
                    "title": "Proper Gear",
                    "desc": "Invest in comfortable, supportive workout clothing and breastfeeding-friendly activewear.",
                    "icon": "fa-solid fa-shirt"
                }
            ]
        },
        "results": {
            "heading": "Expected Results",
            "subheading": "What to expect from your postnatal weight loss journey",
            "para": "With our sustainable approach, you can achieve meaningful, lasting results.",
            "items": [
                {
                    "title": "Gradual Progress",
                    "desc": "Healthy weight loss of 0.5-1kg per week in the first six months postpartum.",
                    "icon": "fa-solid fa-weight-scale"
                },
                {
                    "title": "Improved Fitness",
                    "desc": "Rebuilt muscle tone, core strength and stamina through safe exercises.",
                    "icon": "fa-solid fa-dumbbell"
                },
                {
                    "title": "Body Confidence",
                    "desc": "Regained sense of self and improved body image as you achieve your goals.",
                    "icon": "fa-solid fa-face-smile"
                },
                {
                    "title": "Emotional Wellbeing",
                    "desc": "Reduced stress and anxiety through the combination of physical activity and achieving personal milestones.",
                    "icon": "fa-solid fa-heart"
                }
            ]
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "Common questions about postnatal weight loss",
            "items": [
                {
                    "question": "How soon can I start losing weight after childbirth?",
                    "answer": "It's advisable to wait until your 6-week postpartum check-up before starting any weight loss regimen. Your doctor can give you the green light depending on your recovery."
                },
                {
                    "question": "Can I lose weight while breastfeeding?",
                    "answer": "Yes, breastfeeding can actually help with weight loss as it burns extra calories. However, it's important to consume enough nutrients to support both you and your baby."
                },
                {
                    "question": "What types of exercises are safe postpartum?",
                    "answer": "Low-impact exercises such as walking, yoga, swimming, and pelvic floor exercises are great options to start with. As your strength improves, you can gradually increase the intensity."
                },
                {
                    "question": "How long does it take to lose post-pregnancy weight?",
                    "answer": "Every woman's body is different, but it's generally recommended to aim for losing 0.5 to 1 kg per week. Patience and consistency are key."
                },
                {
                    "question": "Are weight loss injections safe for breastfeeding mothers?",
                    "answer": "Some injections may be safe while others aren't recommended during breastfeeding. We carefully evaluate each case and recommend only treatments that are compatible with nursing."
                },
                {
                    "question": "How does Elite Body Home's approach differ from regular weight loss programs?",
                    "answer": "Our program is specifically designed for postpartum bodies, considering hormonal changes, recovery needs, and the unique challenges new mothers face. We combine medical expertise with compassionate support."
                }
            ]
        }
    },
    "ar": {
        "url": "postnatal-weight-loss",
        "meta": {
            "title": "برنامج فقدان الوزن بعد الولادة في دبي | إيليت بودي هوم",
            "description": "برنامج متخصص لفقدان الوزن بعد الولادة في دبي، مصمم خصيصًا للأمهات الجدد. نقدم نهجًا شخصيًا يجمع بين التغذية السليمة، التمارين الآمنة والعلاجات غير الجراحية لمساعدتك في استعادة ثقتك بنفسك.",
            "robots": "index, follow",
            "canonical": "https://elitebodyhome.com/services/postnatal-weight-loss-in-dubai/",
            "og": {
                "title": "برنامج فقدان الوزن بعد الولادة في دبي | إيليت بودي هوم",
                "description": "برنامج متخصص لفقدان الوزن بعد الولادة في دبي، مصمم خصيصًا للأمهات الجدد. نقدم نهجًا شخصيًا يجمع بين التغذية السليمة، التمارين الآمنة والعلاجات غير الجراحية.",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Postnatal-Weight-Loss-1.webp"
            }
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "فقدان الوزن بعد الولادة"],
        "hero": {
            "title": "برنامج فقدان الوزن بعد الولادة في دبي",
            "subtitle": "استعيدي ثقتك بنفسك!",
            "description": "في إيليت بودي هوم، نتفهم التحديات الفريدة التي تواجهها الأمهات الجدد في رحلة فقدان الوزن بعد الولادة. نقدم برنامجًا متخصصًا مصممًا لمساعدتك على فقدان الوزن بطريقة صحية من خلال علاجات شخصية وإرشادات خبراء. سواء كنتِ بحاجة إلى خطة تغذية مخصصة أو علاجات غير جراحية لتنسيق القوام، نقدم حلولاً مستدامة في بيئة داعمة."
        },
        "howItWorks": {
            "title": "ما هو فقدان الوزن بعد الولادة؟",
            "description": "يجب أن يكون فقدان الوزن بعد الولادة تدريجيًا، مما يمنح جسمك الوقت الكافي للتعافي. فقدان الوزن السريع أو الحميات القاسية قد يكون ضارًا، خاصة للأمهات المرضعات. نقدم نهجًا يجمع بين الأنظمة الغذائية الغنية بالعناصر الغذائية والتمارين المناسبة لدعم فقدان الوزن والصحة العامة بعد الولادة."
        },
        "procedureSteps": {
            "heading": "خطوات برنامجنا لفقدان الوزن بعد الولادة",
            "para": "فقدان وزن الحمل يتطلب نهجًا متوازنًا يجمع بين النظام الغذائي، التمارين وتغييرات نمط الحياة لتحقيق نتائج دائمة.",
            "steps": [
                {
                    "title": "التقييم الشخصي",
                    "desc": "نبدأ بتقييم شامل لصحتك بعد الولادة، أهداف فقدان الوزن وعوامل نمط حياتك.",
                    "icon": "fa-solid fa-clipboard-user"
                },
                {
                    "title": "التخطيط الغذائي",
                    "desc": "يضع خبراؤنا خطة وجبات مخصصة غنية بالأطعمة الكاملة لدعم احتياجات الطاقة وفقدان الوزن الصحي.",
                    "icon": "fa-solid fa-utensils"
                },
                {
                    "title": "إرشادات التمارين",
                    "desc": "نوصي بتمارين آمنة منخفضة التأثير مصممة حسب مرحلة تعافيك بعد الولادة ومستوى لياقتك.",
                    "icon": "fa-solid fa-person-walking"
                },
                {
                    "title": "الدعم المستمر",
                    "desc": "متابعات دورية لضبط خطتك، الاحتفال بالتقدم ومعالجة أي تحديات تواجهينها.",
                    "icon": "fa-solid fa-hands-holding-heart"
                }
            ]
        },
        "benefits": {
            "heading": "فوائد فقدان الوزن بعد الولادة",
            "subheading": "لماذا التركيز على إدارة الوزن الصحية بعد الولادة؟",
            "para": "تحقيق وزن صحي بعد الولادة يوفر العديد من المزايا الصحية الجسدية والعقلية تتجاوز المظهر الخارجي.",
            "items": [
                {
                    "title": "تحسين مستويات الطاقة",
                    "desc": "فقدان الوزن الزائد يعزز الطاقة للتعامل مع متطلبات الأمومة.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "تقليل المخاطر الصحية",
                    "desc": "يقلل من فرص الإصابة بحالات مثل السكري، أمراض القلب وارتفاع ضغط الدم المرتبطة بزيادة وزن الحمل.",
                    "icon": "fa-solid fa-heart-pulse"
                },
                {
                    "title": "تعزيز الصحة العقلية",
                    "desc": "تحقيق أهداف الوزن يمكن أن يعزز الثقة بالنفس ويخفف من اكتئاب ما بعد الولادة أو القلق.",
                    "icon": "fa-solid fa-brain"
                },
                {
                    "title": "تحسين الحركة",
                    "desc": "يسهل ممارسة الأنشطة البدنية ويساعد في استعادة اللياقة البدنية لما قبل الحمل.",
                    "icon": "fa-solid fa-person-running"
                }
            ]
        },
        "injectionOptions": {
            "heading": "خيارات حقن فقدان الوزن",
            "subheading": "حلول آمنة تحت الإشراف الطبي لتكملة فقدان الوزن بعد الولادة",
            "para": "نقدم أنواعًا مختلفة من حقن فقدان الوزن المصممة حسب الاحتياجات والأهداف الفردية بعد الولادة.",
            "items": [
                {
                    "title": "حقن حرق الدهون",
                    "desc": "تحفز عملية الأيض لاستهداف الدهون العنيدة في مناطق مثل البطن والفخذين مع الحفاظ على أمانها للأجسام بعد الولادة.",
                    "icon": "fa-solid fa-fire-flame-curved"
                },
                {
                    "title": "حقن ليبوتروبيك",
                    "desc": "تركيبات غنية بالفيتامينات تعزز أيض الدهون ووظائف الكبد مع دعم مستويات الطاقة للأمهات الجدد.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "أكواليكس لإذابة الدهون",
                    "desc": "علاج غير جراحي يذيب الدهون في مناطق محددة مثل الذقن والفخذين، مثالي لاستهداف المناطق المشكلة بعد الولادة.",
                    "icon": "fa-solid fa-droplet"
                },
                {
                    "title": "العلاج بالوريد التغذوي",
                    "desc": "يعوض الفيتامينات والمعادن الأساسية التي قد تنضب بعد الحمل والولادة.",
                    "icon": "fa-solid fa-iv"
                }
            ]
        },
        "eligibility": {
            "heading": "أهلية فقدان الوزن بعد الولادة",
            "subheading": "متى يمكنك بدء رحلة فقدان الوزن بعد الولادة بأمان؟",
            "para": "معظم النساء يمكنهن بدء فقدان الوزن بلطف بعد الولادة، لكن التوقيت يختلف حسب العوامل الفردية.",
            "items": [
                {
                    "title": "الولادة الطبيعية",
                    "desc": "يمكن عادةً بدء تمارين خفيفة خلال بضعة أسابيع بعد الولادة، بعد الحصول على موافقة طبية.",
                    "icon": "fa-solid fa-baby"
                },
                {
                    "title": "الولادة القيصرية",
                    "desc": "تتطلب عادةً فترة تعافي من 6-8 أسابيع قبل بدء التمارين، حسب تقدم الشفاء.",
                    "icon": "fa-solid fa-hospital-user"
                },
                {
                    "title": "الأمهات المرضعات",
                    "desc": "يحتجن إلى اعتبارات غذائية خاصة للحفاظ على إدرار الحمعان فقدان الوزن بأمان.",
                    "icon": "fa-solid fa-bottle-baby"
                }
            ]
        },
        "preparationTips": {
            "heading": "الاستعداد لفقدان الوزن بعد الولادة",
            "subheading": "تهيئة نفسك للنجاح",
            "para": "الإعداد السليم يجعل رحلة فقدان الوزن بعد الولادة أكثر أمانًا وفعالية.",
            "items": [
                {
                    "title": "الموافقة الطبية",
                    "desc": "استشيري طبيبك دائمًا قبل بدء أي برنامج لفقدان الوزن بعد الولادة.",
                    "icon": "fa-solid fa-user-doctor"
                },
                {
                    "title": "وضع أهداف واقعية",
                    "desc": "اهدفي إلى فقدان وزن تدريجي ومستدام بدلاً من نتائج سريعة.",
                    "icon": "fa-solid fa-bullseye"
                },
                {
                    "title": "نظام الدعم",
                    "desc": "ابني شبكة من العائلة، الأصدقاء أو الأمهات الجدد للحصول على الدافع والمساءلة.",
                    "icon": "fa-solid fa-people-group"
                },
                {
                    "title": "المعدات المناسبة",
                    "desc": "استثمري في ملابس رياضية مريحة وداعمة وملابس رياضية مناسبة للرضاعة.",
                    "icon": "fa-solid fa-shirt"
                }
            ]
        },
        "results": {
            "heading": "النتائج المتوقعة",
            "subheading": "ما يمكن توقعه من رحلة فقدان الوزن بعد الولادة",
            "para": "مع نهجنا المستدام، يمكنك تحقيق نتائج ذات معنى ودائمة.",
            "items": [
                {
                    "title": "تقدم تدريجي",
                    "desc": "فقدان وزن صحي بمعدل 0.5-1 كجم أسبوعيًا في الأشهر الستة الأولى بعد الولادة.",
                    "icon": "fa-solid fa-weight-scale"
                },
                {
                    "title": "تحسن اللياقة",
                    "desc": "إعادة بناء قوة العضلات، القوة الأساسية والقدرة على التحمل من خلال تمارين آمنة.",
                    "icon": "fa-solid fa-dumbbell"
                },
                {
                    "title": "ثقة الجسم",
                    "desc": "استعادة الشعور بالذات وتحسين صورة الجسم مع تحقيق الأهداف.",
                    "icon": "fa-solid fa-face-smile"
                },
                {
                    "title": "الرفاهية العاطفية",
                    "desc": "تقليل التوتر والقلق من خلال الجمع بين النشاط البدني وتحقيق المعالم الشخصية.",
                    "icon": "fa-solid fa-heart"
                }
            ]
        },
        "faqData": {
            "heading": "الأسئلة الشائعة",
            "subheading": "أسئلة شائعة حول فقدان الوزن بعد الولادة",
            "items": [
                {
                    "question": "متى يمكنني البدء في فقدان الوزن بعد الولادة؟",
                    "answer": "من المستحسن الانتظار حتى فحص ما بعد الولادة بعد 6 أسابيع قبل البدء في أي نظام لفقدان الوزن. يمكن لطبيبك إعطاء الضوء الأخضر حسب تعافيك."
                },
                {
                    "question": "هل يمكنني فقدان الوزن أثناء الرضاعة الطبيعية؟",
                    "answer": "نعم، يمكن أن تساعد الرضاعة الطبيعية في فقدان الوزن لأنها تحرق سعرات حرارية إضافية. ومع ذلك، من المهم استهلاك ما يكفي من العناصر الغذائية لدعمك أنت وطفلك."
                },
                {
                    "question": "ما أنواع التمارين الآمنة بعد الولادة؟",
                    "answer": "التمارين منخفضة التأثير مثل المشي، اليوغا، السباحة وتمارين قاع الحوض هي خيارات رائعة للبدء بها. مع تحسن قوتك، يمكنك زيادة الكثافة تدريجيًا."
                },
                {
                    "question": "كم من الوقت يستغرق فقدان وزن الحمل؟",
                    "answer": "كل جسم امرأة مختلف، ولكن يُنصح عمومًا بالهدف لفقدان 0.5 إلى 1 كجم أسبوعيًا. الصبر والاستمرارية هما المفتاح."
                },
                {
                    "question": "هل حقن فقدان الوزن آمنة للأمهات المرضعات؟",
                    "answer": "بعض الحقن قد تكون آمنة بينما لا ينصح ببعضها أثناء الرضاعة الطبيعية. نقيم كل حالة بعناية ونوصي فقط بالعلاجات المتوافقة مع الرضاعة."
                },
                {
                    "question": "كيف يختلف نهج إيليت بودي هوم عن برامج فقدان الوزن العادية؟",
                    "answer": "برنامجنا مصمم خصيصًا لأجسام ما بعد الولادة، مع الأخذ في الاعتبار التغيرات الهرمونية، احتياجات التعافي والتحديات الفريدة التي تواجهها الأمهات الجدد. نجمع بين الخبرة الطبية والدعم الرحيم."
                }
            ]
        }
    },
    "shared": {
        "images": {
            "main": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/ebh-services-uae-40-scaled.jpg",
            "beforeAfter": [
                {
                    "src": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/106.webp",
                    "alt": "Postnatal weight loss before and after"
                },
                {
                    "src": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Postnatal-Weight-Loss-2.webp",
                    "alt": "Postnatal weight loss before and after"
                },
                {
                    "src": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Postnatal-Weight-Loss-1.webp",
                    "alt": "Postnatal weight loss before and after"
                }
            ]
        },
        "product": {
            "id": 167,
            "title": "Postnatal Weight Loss Program",
            "description": "Comprehensive postnatal weight loss plan designed to restore strength, promote healthy weight loss, and boost confidence for new mothers.",
            "price": 199
        }
    }
}